import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TagsApiAdapter,
  TagsListParams,
} from '../adapters/TagsApiAdapter';
import {
  CreateTagRequest,
  UpdateTagRequest,
  ModifyOrderTagsRequest,
} from '../models/CRM';

const tagsListParamsToQueryKey = (params: TagsListParams) => [
  'crm-tags',
  params.page ?? 1,
  params.per_page ?? 15,
];

export const useTags = (params: TagsListParams = {}, enabled = true) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new TagsApiAdapter(httpClient);

  const query = useQuery({
    queryKey: tagsListParamsToQueryKey(params),
    queryFn: () => apiAdapter.getTags(params),
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    tags: query.data?.tags ?? [],
    total: query.data?.total ?? 0,
    currentPage: query.data?.currentPage ?? 1,
    lastPage: query.data?.lastPage ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useCreateTag = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new TagsApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTagRequest) => apiAdapter.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-tags'] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });
};

export const useModifyOrderTags = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new TagsApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ModifyOrderTagsRequest) =>
      apiAdapter.modifyOrderTags(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-tags'] });
      // No invalidamos 'crm-orders' para evitar refetch automático (actualizamos localmente)
    },
  });
};

export const useUpdateTag = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new TagsApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, data }: { tagId: number; data: UpdateTagRequest }) =>
      apiAdapter.updateTag(tagId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-tags'] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });
};

export const useDeleteTag = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new TagsApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: number) => apiAdapter.deleteTag(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-tags'] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });
};
